import { useLazyQuery } from '@apollo/client';
import {
    GetClassroomResponse,
    GetClassroomVariables,
    GET_CLASSROOM,
    GetClassroomResultsResponse,
    GetClassroomResultsVariables,
    GET_CLASSROOM_RESULTS,
    GetHomeworkResponse,
    GetHomeworkVariables,
    GetHomeworkResultsResponse,
    GetHomeworkResultsVariables,
    GET_HOMEWORK,
    GET_HOMEWORK_RESULTS,
    GET_RESULT_FILES,
    GetResultFilesResponse,
} from './gql';
import { formatName } from '@/utils/name';
import { useCallback, useEffect, useState } from 'react';
import { StudentResult } from '../types/result';
import { isQuestionComponentType, parseSlide } from '@/types/slide';
import { getScore } from '@/utils/score';

export const useStudentResults = (src: { type: 'classroom' | 'homework'; id: string }) => {
    // classrom result gql
    const [getClassroom, { loading: loadingClassroom, data: classroom }] = useLazyQuery<GetClassroomResponse, GetClassroomVariables>(GET_CLASSROOM, {
        variables: { where: { id: src.id } },
        fetchPolicy: 'network-only',
    });
    const [getClassroomResults, { loading: loadingClassroomResults, data: classroomResults }] = useLazyQuery<GetClassroomResultsResponse, GetClassroomResultsVariables>(GET_CLASSROOM_RESULTS, {
        variables: { where: { testroom: { id: { equals: src.id } } } },
        fetchPolicy: 'network-only',
    });

    // homework result gql
    const [getHomework, { loading: loadingHomework, data: homework }] = useLazyQuery<GetHomeworkResponse, GetHomeworkVariables>(GET_HOMEWORK, {
        variables: { where: { id: src.id } },
        fetchPolicy: 'network-only',
    });
    const [getHomeworkResults, { loading: loadingHomeworkResults, data: homeworkResults }] = useLazyQuery<GetHomeworkResultsResponse, GetHomeworkResultsVariables>(GET_HOMEWORK_RESULTS, {
        variables: { where: { homework: { id: { equals: src.id } } } },
        fetchPolicy: 'network-only',
    });

    const [getResultFiles] = useLazyQuery<GetResultFilesResponse>(GET_RESULT_FILES);

    // student result state
    const [studentResults, setStudentResults] = useState<Record<string, StudentResult>>({});
    const [totalQuestionCount, setTotalQuestionCount] = useState(0);

    // fetch classroom or homework data
    const refetch = useCallback(() => {
        if (src.type === 'classroom') {
            getClassroom();
            getClassroomResults();
        } else {
            getHomework();
            getHomeworkResults();
        }
    }, [getClassroom, getClassroomResults, getHomework, getHomeworkResults, src.type]);

    // init and upon src.type change to refetch student results
    useEffect(() => {
        refetch();
    }, [refetch]);

    // parse classroom or homework data
    useEffect(() => {
        const slides = src.type === 'classroom' ? classroom?.testroom.lessonVersion.slides : homework?.homework.lessonVersion.slides;
        const students = src.type === 'classroom' ? classroom?.testroom.class.students : homework?.homework.class.map((c) => c.students).flat();
        const results = src.type === 'classroom' ? classroomResults?.results : homeworkResults?.homeworkResults;

        if (slides && students && results) {
            const sortedSlides = [...slides]
                .sort((a, b) => a.sort - b.sort)
                .map((slide) => {
                    const parsedSlide = parseSlide(slide);
                    return {
                        ...slide,
                        questionCount: parsedSlide.data.filter(isQuestionComponentType).length,
                    };
                });

            // calculate total question count
            const totalQuestionCount = sortedSlides.reduce((acc, slide) => acc + slide.questionCount, 0);

            const studentResults = students.reduce(
                (acc, student) => {
                    acc[student.id] = {
                        id: student.id,
                        studentName: formatName(student.familyName, student.givenName),
                        studentNumber: student.studentNumber,
                        className: classroom?.testroom.class.name || '',
                        score: 0,
                        timeSpent: 0,
                        questionCount: totalQuestionCount,
                        responseCount: 0,
                        slides: sortedSlides.map((rawSlide) => {
                            const slide = parseSlide(rawSlide);

                            return {
                                ...slide,
                                questions: slide.data.filter(isQuestionComponentType).map((question) => {
                                    const answers = [...results].filter((r) => r.slide.id === slide.id && student.id === r.student.id && question.id === r.componentId);
                                    return {
                                        ...question,
                                        answers: answers
                                            .sort((a, b) => new Date(b.endTime).getTime() - new Date(a.endTime).getTime())
                                            .map((answer) => {
                                                return {
                                                    ...answer,
                                                    fetchResultFiles: () =>
                                                        getResultFiles({
                                                            variables: {
                                                                where: { result: { id: { equals: answer.id } } },
                                                            },
                                                        }).then(async (response) => {
                                                            return response.data?.resultFiles.map((result) => ({ url: result.file.url })) || [];
                                                        }),
                                                };
                                            }),
                                    };
                                }),
                                score: 0,
                            };
                        }),
                    };

                    return acc;
                },
                {} as Record<string, StudentResult>,
            );

            // calculate time spent
            for (const result of results) {
                const student = studentResults[result.student.id];
                if (student) {
                    student.timeSpent += new Date(result.endTime).getTime() - new Date(result.startTime).getTime();
                }
            }

            // calculate score
            for (const student of Object.values(studentResults)) {
                for (const slide of student.slides) {
                    slide.score = slide.questions.reduce((acc, question) => {
                        return acc + getScore(question);
                    }, 0);
                }

                student.score = student.slides.reduce((acc, slide) => {
                    return acc + slide.score;
                }, 0);
            }

            // calculate response count
            for (const student of Object.values(studentResults)) {
                for (const slide of student.slides) {
                    student.responseCount += slide.questions.reduce((acc, question) => {
                        return acc + (question.answers.length > 0 ? 1 : 0);
                    }, 0);
                }
            }

            setTotalQuestionCount(totalQuestionCount);
            setStudentResults(studentResults);
        }
    }, [classroom, classroomResults, getResultFiles, homework, homeworkResults, src.type]);

    return {
        loading: src.type === 'classroom' ? loadingClassroom || loadingClassroomResults : loadingHomework || loadingHomeworkResults,
        results: studentResults,
        totalQuestionCount: totalQuestionCount,
        refetch: refetch,
    };
};
